import { useQuery } from "@tanstack/react-query";
import { LocalDate } from "@js-joda/core";
import React from "react";
import { Page } from "../../activities/AppPage";
import { API } from "../../API";
import useCaregiver from "../../hooks/useCaregiver";
import { useSocket } from "../../hooks/useSocket";
import { isLoading } from "../../Loading";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import { translate } from "../../translate";
import ErrorScaffold from "../ErrorScaffold";
import Spinner from "../Generic/Spinner";
import useInstaPayVisitItemClickHandler from "../InstaPay/hooks/useInstaPayVisitItemClickHandler";
import LoadingScaffold from "../LoadingScaffold";
import InstaPayHistoryScreen from "./InstaPayHistoryScreen";

interface Props {
  api: API;
  appState: AppState;
  onRequestNavigate: (page: Page) => void;
  onClickGoBack: () => void;
}

export default function InstaPayHistoryActivity(props: Props) {
  const [currentWeek, setCurrentWeek] = React.useState<number>(0);
  const caregiver = useCaregiver(props.appState);

  const payrollQuery = useQuery({
    queryKey: ["instapay", "history", currentWeek],
    queryFn: () => props.api.getPayrollInformation(currentWeek),
    keepPreviousData: true,
  });

  useSocket({
    eventName: "InstapaySuccess",
    onEvent: () => {
      payrollQuery.refetch();
    },
  });

  const handleClickVisit = useInstaPayVisitItemClickHandler({
    caregiver: caregiver,
    currentWeek: currentWeek,
    onRequestNavigate: props.onRequestNavigate,
  });

  if (payrollQuery.isLoading || isLoading(caregiver)) {
    return (
      <LoadingScaffold
        title={translate(STRINGS.INSTAPAY_HISTORY)}
        onClickGoBack={props.onClickGoBack}
      />
    );
  }

  if (payrollQuery.isError) {
    return (
      <ErrorScaffold
        title={translate(STRINGS.OOPS)}
        message={translate(STRINGS.SOMETHING_WENT_WRONG)}
        onClickGoBack={props.onClickGoBack}
      />
    );
  }

  const spinner = <Spinner style={{ display: "flex" }} height={22} width={104} />;

  const weekLabel = (() => {
    if (payrollQuery.isFetching) {
      return spinner;
    }

    const format = (date: LocalDate) => `${date.monthValue()}/${date.dayOfMonth()}`;
    const from = format(payrollQuery.data.nextWeek.minusDays(7));
    const to = format(payrollQuery.data.nextWeek.minusDays(1));

    return `${from} - ${to}`;
  })();

  return (
    <InstaPayHistoryScreen
      data={payrollQuery.data}
      weekLabel={weekLabel}
      onClickPreviousWeek={() => setCurrentWeek((prev) => prev - 1)}
      onClickNextWeek={() => setCurrentWeek((prev) => prev + 1)}
      onClickHistory={() => props.onRequestNavigate({ type: "InstaPayHistory" })}
      onClickGoBack={props.onClickGoBack}
      onClickVisit={handleClickVisit}
    />
  );
}
